/**
 * UtilitySection - Per-scenario tariff selection for EV projects.
 *
 * Shows current tariff status (scenario override vs project default)
 * and allows selecting/clearing a scenario-level tariff.
 */

import React, { useCallback } from 'react';
import { Alert, Box, Button } from '@mui/material';
import { ElectricBolt as ElectricBoltIcon } from '@mui/icons-material';
import { SectionCard, UnifiedTariffSelector } from '@vortx/shared-ui';

function UtilitySection({ scenario, projectData, onChange }) {
    const scenarioTariffId = scenario?.tariff_id;
    const scenarioTariffName = scenario?.tariff_name;
    const projectTariffId = projectData?.tariff_id;
    const projectTariffName = projectData?.tariff_name;

    const handleTariffSelect = useCallback((tariff) => {
        onChange({
            tariff_id: tariff.id,
            tariff_name: tariff.name || tariff.label,
        });
    }, [onChange]);

    const handleClear = useCallback(() => {
        onChange({
            tariff_id: null,
            tariff_name: null,
        });
    }, [onChange]);

    const renderStatus = () => {
        if (scenarioTariffId) {
            return (
                <Alert
                    severity="success"
                    action={
                        <Button
                            color="inherit"
                            size="small"
                            onClick={handleClear}
                        >
                            Clear
                        </Button>
                    }
                    sx={{ mb: 2 }}
                >
                    Scenario tariff: <strong>{scenarioTariffName}</strong>
                    {projectTariffId && ' (overrides project default)'}
                </Alert>
            );
        }

        if (projectTariffId) {
            return (
                <Alert severity="info" sx={{ mb: 2 }}>
                    Using project tariff: <strong>{projectTariffName}</strong>.
                    Select below to override for this scenario.
                </Alert>
            );
        }

        return (
            <Alert severity="warning" sx={{ mb: 2 }}>
                Select a tariff to enable cost-optimized charging.
            </Alert>
        );
    };

    return (
        <SectionCard title="Utility & Tariff" icon={<ElectricBoltIcon />}>
            {renderStatus()}
            <Box>
                <UnifiedTariffSelector
                    selectedTariff={
                        scenarioTariffId
                            ? { id: scenarioTariffId, name: scenarioTariffName }
                            : null
                    }
                    onTariffSelect={handleTariffSelect}
                />
            </Box>
        </SectionCard>
    );
}

export default UtilitySection;
